import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  InputLabel,
  Switch,
  Tooltip,
} from "@mui/material";
import MainCard from "components/MainCard";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EmailSchema, IEmailSettings } from "../api/types";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { showError, showSuccess } from "utils/showMessages";
import { ApiResponse } from "common/models/ApiResponse";
import { showServerErrors } from "utils/formUtils";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "components/Loader";
import { UpdateEmailSettings } from "../api/mutation";
import { GetEmailSettings } from "../api/hooks";
import { Stack } from "@mui/material";
import { InfoCircleFilled } from "@ant-design/icons";

const EmailSettingsTab = () => {
  const { libraryID } = useParams();
  const editMode = libraryID !== undefined;
  const navigateTo = useNavigate();
  const queryClient = useQueryClient();

  const updateMutation = UpdateEmailSettings();
  const {
    handleSubmit,
    setError,
    register,
    reset,
    formState: { isSubmitting },
  } = useForm<IEmailSettings>({
    resolver: zodResolver(EmailSchema),
  });

  const [issueEmailChecked, setIssueEmailChecked] = useState<boolean>(false);
  const handleIssueEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIssueEmailChecked(event.target.checked);
  };

  const [returnEmailChecked, setReturnEmailChecked] = useState<boolean>(false);
  const handleReturnEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setReturnEmailChecked(event.target.checked);
  };
  const [renewEmailChecked, setRenewEmailChecked] = useState<boolean>(false);
  const handleRenewEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRenewEmailChecked(event.target.checked);
  };

  const [reservationReadyEmailChecked, setReservationReadyEmailChecked] =
    useState<boolean>(false);
  const handleReservationReadyEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setReservationReadyEmailChecked(event.target.checked);
  };

  const [reservationExpiredEmailChecked, setReservationExpiredEmailChecked] =
    useState<boolean>(false);
  const handleReservationExpiredEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setReservationExpiredEmailChecked(event.target.checked);
  };

  const getEmailSettingsQuery = GetEmailSettings(libraryID || "");
  useEffect(() => {
    if (getEmailSettingsQuery?.data) {
      reset(getEmailSettingsQuery.data.apiData);
      setIssueEmailChecked(getEmailSettingsQuery.data.apiData.isIssuedEmail);
      setReturnEmailChecked(getEmailSettingsQuery.data.apiData.isReturnedEmail);
      setRenewEmailChecked(getEmailSettingsQuery.data.apiData.isRenewEmail);
      setReservationReadyEmailChecked(
        getEmailSettingsQuery.data.apiData.isReservationReadyEmail,
      );
      setReservationExpiredEmailChecked(
        getEmailSettingsQuery.data.apiData.isReservationExpiredEmail,
      );
    }
  }, [getEmailSettingsQuery.data, reset]);

  const onSubmit = handleSubmit((data) => {
    if (isSubmitting) return;
    data.libraryID = libraryID || "";

    updateMutation.mutate(data, {
      onSettled(response) {
        handleResponse(response);
      },
    });
  });

  const handleResponse = async (response: ApiResponse<number> | undefined) => {
    if (response?.status == 200) {
      if (editMode) {
        queryClient.invalidateQueries({
          queryKey: ["library", "getEmailSettings", "EmailSettings"],
        });
      }
      showSuccess(`Record ${editMode ? "updated" : "added"} successfully`);
      navigateTo(`../edit/${libraryID}/email`);
    } else {
      if (response?.status == 404) {
        showError(response?.title ?? "");
      } else if (response?.status == 400) {
        if (response?.modelErrors) {
          showServerErrors(setError, response.errors);
          showError(response?.title ?? "");
        } else {
          showError(response?.title ?? "");
        }
      } else {
        showError(response?.title ?? "");
      }
    }
  };

  return (
    <MainCard
      content={false}
      darkTitle
      darktitleVariant={"h4"}
      title={editMode ? "Email Settings" : "Add Library"}
    >
      <Box component={"form"} sx={{ p: 2.5 }} onSubmit={onSubmit}>
        {getEmailSettingsQuery.isFetching ? <Loader /> : <></>}
        <Grid container spacing={2} direction={"column"}>
          <Grid container item xs={12} md={6} lg={6} alignItems="center">
            <Grid item xs={10} sm={8} md={8} lg={6}>
              <InputLabel htmlFor="isIssuedEmail">
                <Stack direction={"row"} alignItems={"center"} spacing={1}>
                  <Stack>Do you want to send mail after the book issued?</Stack>
                  <Stack>
                    <Tooltip
                      title="A mail will send to member after issue (If Enabled)"
                      arrow
                    >
                      <InfoCircleFilled />
                    </Tooltip>
                  </Stack>
                </Stack>
              </InputLabel>
            </Grid>
            <Grid item xs={2} sm={4} md={4} lg={2}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    {...register("isIssuedEmail")}
                    checked={issueEmailChecked}
                    onChange={handleIssueEmailChange}
                    id="isIssuedEmail"
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label=""
              />
            </Grid>
          </Grid>
          <Divider sx={{ py: 1 }} />
          <Grid container item xs={12} md={6} lg={6} alignItems="center">
            <Grid item xs={10} sm={8} md={8} lg={6}>
              <InputLabel htmlFor="isReturnedEmail">
                <Stack direction={"row"} alignItems={"center"} spacing={1}>
                  <Stack>
                    Do you want to send email after the book is returned?
                  </Stack>
                  <Stack>
                    <Tooltip
                      title="A mail will send to member after return (If Enabled)"
                      arrow
                    >
                      <InfoCircleFilled />
                    </Tooltip>
                  </Stack>
                </Stack>
              </InputLabel>
            </Grid>
            <Grid item xs={2} sm={4} md={4} lg={2}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    {...register("isReturnedEmail")}
                    checked={returnEmailChecked}
                    onChange={handleReturnEmailChange}
                    id="isReturnedEmail"
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label=""
              />
            </Grid>
          </Grid>
          <Divider sx={{ py: 1 }} />
          <Grid container item xs={12} md={6} lg={6} alignItems="center">
            <Grid item xs={10} sm={8} md={8} lg={6}>
              <InputLabel htmlFor="isRenewEmail">
                <Stack direction={"row"} alignItems={"center"} spacing={1}>
                  <Stack>
                    Do you want to send email after the book is renew?
                  </Stack>
                  <Stack>
                    <Tooltip
                      title="A mail will send to member after renew (If Enabled)"
                      arrow
                    >
                      <InfoCircleFilled />
                    </Tooltip>
                  </Stack>
                </Stack>
              </InputLabel>
            </Grid>
            <Grid item xs={2} sm={4} md={4} lg={2}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    {...register("isRenewEmail")}
                    checked={renewEmailChecked}
                    onChange={handleRenewEmailChange}
                    id="isRenewEmail"
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label=""
              />
            </Grid>
          </Grid>
          <Divider sx={{ py: 1 }} />
          <Grid container item xs={12} md={6} lg={6} alignItems="center">
            <Grid item xs={10} sm={8} md={8} lg={6}>
              <InputLabel htmlFor="isReservationReadyEmail">
                <Stack direction={"row"} alignItems={"center"} spacing={1}>
                  <Stack>
                    Do you want to send email when a reserved book is ready
                    for pickup?
                  </Stack>
                  <Stack>
                    <Tooltip
                      title="A mail will be sent to the member as soon as a copy is set aside for their reservation (If Enabled)"
                      arrow
                    >
                      <InfoCircleFilled />
                    </Tooltip>
                  </Stack>
                </Stack>
              </InputLabel>
            </Grid>
            <Grid item xs={2} sm={4} md={4} lg={2}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    {...register("isReservationReadyEmail")}
                    checked={reservationReadyEmailChecked}
                    onChange={handleReservationReadyEmailChange}
                    id="isReservationReadyEmail"
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label=""
              />
            </Grid>
          </Grid>
          <Divider sx={{ py: 1 }} />
          <Grid container item xs={12} md={6} lg={6} alignItems="center">
            <Grid item xs={10} sm={8} md={8} lg={6}>
              <InputLabel htmlFor="isReservationExpiredEmail">
                <Stack direction={"row"} alignItems={"center"} spacing={1}>
                  <Stack>
                    Do you want to send email when a reservation hold
                    expires?
                  </Stack>
                  <Stack>
                    <Tooltip
                      title="A mail will be sent to the member if their reservation hold expires before they collect it (If Enabled)"
                      arrow
                    >
                      <InfoCircleFilled />
                    </Tooltip>
                  </Stack>
                </Stack>
              </InputLabel>
            </Grid>
            <Grid item xs={2} sm={4} md={4} lg={2}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    {...register("isReservationExpiredEmail")}
                    checked={reservationExpiredEmailChecked}
                    onChange={handleReservationExpiredEmailChange}
                    id="isReservationExpiredEmail"
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label=""
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid container item spacing={2} mb={0} pt={2}>
          <Grid item sm={12}>
            <Button
              title={editMode ? "Update" : "Save"}
              type="submit"
              disabled={isSubmitting}
              variant="contained"
            >
              {isSubmitting && (
                <CircularProgress color="inherit" size="1rem" sx={{ mr: 1 }} />
              )}
              {editMode ? "Update" : "Save"}
            </Button>
            <Button
              title="Back"
              sx={{ ml: 1 }}
              variant="contained"
              color="secondary"
              disabled={isSubmitting}
              onClick={() => navigateTo(`/admin/library`)}
            >
              Back
            </Button>
          </Grid>
        </Grid>
      </Box>
    </MainCard>
  );
};
export default EmailSettingsTab;
